import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';
import { ConversationSummary } from '../../models/conversation-summary';
import { ConversationListService, ConversationListState } from '../../services/conversation/conversation-list.service';
import { ConversationManager } from '../../services/conversation/conversation-manager.service';

/**
 * Chat history sidebar component for displaying and managing conversation history.
 * FIN-0102: Chat History Feature
 *
 * Design decisions:
 * - Left-side drawer (industry standard: ChatGPT, Claude, Gemini)
 * - Mobile: 75-80% width overlay with backdrop
 * - Desktop: 280px fixed width, can be toggled
 * - Uses ConversationManager.currentConversationId$ as single source of truth (FIN-0065)
 */
@Component({
  selector: 'app-chat-history-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule],
  templateUrl: './chat-history-sidebar.component.html',
  styleUrls: ['./chat-history-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ChatHistorySidebarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() conversationSelected = new EventEmitter<string>();
  @Output() newConversationRequested = new EventEmitter<void>();

  // State from service
  conversations: ConversationSummary[] = [];
  loading = false;
  hasMore = true;
  error: string | null = null;

  // Active conversation from ConversationManager (FIN-0065 compliance)
  activeConversationId: string | null = null;

  // UI state
  editingId: string | null = null;
  editTitle = '';
  menuOpenId: string | null = null;
  deleteConfirmId: string | null = null;
  deletingId: string | null = null; // Track in-progress deletion
  deleteError: string | null = null;

  // Menu position (for fixed positioning outside scroll viewport)
  menuPosition: { top: number; right: number } = { top: 0, right: 0 };

  private subscriptions: Subscription[] = [];

  constructor(
    private conversationListService: ConversationListService,
    private conversationManager: ConversationManager
  ) {}

  ngOnInit(): void {
    // Subscribe to conversation list state
    this.subscriptions.push(
      this.conversationListService.state$.subscribe((state: ConversationListState) => {
        this.conversations = state.conversations;
        this.loading = state.loading;
        this.hasMore = state.hasMore;
        this.error = state.error;
      })
    );

    // Subscribe to active conversation ID (FIN-0065: single source of truth)
    this.subscriptions.push(
      this.conversationManager.currentConversationId$.subscribe((id: string | null) => {
        this.activeConversationId = id;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When sidebar opens, load conversations
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.onSidebarOpen();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Called when sidebar opens - load conversations
   */
  onSidebarOpen(): void {
    if (this.conversations.length === 0) {
      this.conversationListService.loadConversations();
    }
  }

  /**
   * Close the sidebar
   */
  close(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
    this.closeAllMenus();
  }

  /**
   * Handle backdrop click (mobile)
   */
  onBackdropClick(): void {
    this.close();
  }

  /**
   * Handle escape key
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.menuOpenId) {
      this.menuOpenId = null;
    } else if (this.isOpen) {
      this.close();
    }
  }

  /**
   * Close menu when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // If menu is open and click wasn't on the menu button, close it
    if (this.menuOpenId) {
      const target = event.target as HTMLElement;
      // Check if click was on menu button or inside the floating menu
      if (!target.closest('[aria-label="Conversation options"]') &&
          !target.closest('.fixed.w-36')) {
        this.menuOpenId = null;
      }
    }
  }

  /**
   * Select a conversation
   */
  selectConversation(conversation: ConversationSummary): void {
    // Close any open menus
    this.closeAllMenus();

    // Emit the selection event
    this.conversationSelected.emit(conversation.id);

    // Close sidebar on mobile
    if (window.innerWidth < 768) {
      this.close();
    }
  }

  /**
   * Create new conversation
   */
  createNewConversation(): void {
    this.newConversationRequested.emit();
    this.close();
  }

  /**
   * Load more conversations (infinite scroll) - legacy method for non-virtual scroll
   */
  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

    // Load more when within 100px of bottom
    if (scrollBottom < 100 && !this.loading && this.hasMore) {
      this.conversationListService.loadMore();
    }
  }

  /**
   * Handle virtual scroll index change for infinite loading
   */
  onScrollIndexChange(index: number): void {
    // Load more when within 5 items of the end
    const threshold = this.conversations.length - 5;
    if (index >= threshold && !this.loading && this.hasMore) {
      this.conversationListService.loadMore();
    }
  }

  /**
   * Track by function for virtual scroll performance
   */
  trackByConversationId(index: number, conversation: ConversationSummary): string {
    return conversation.id;
  }

  /**
   * Toggle conversation menu
   */
  toggleMenu(conversationId: string, event: Event): void {
    event.stopPropagation();

    if (this.menuOpenId === conversationId) {
      this.menuOpenId = null;
    } else {
      // Calculate position from the button that was clicked
      const button = event.target as HTMLElement;
      const rect = button.getBoundingClientRect();

      // Position menu below the button, aligned to the right
      this.menuPosition = {
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      };

      this.menuOpenId = conversationId;
    }

    this.editingId = null;
    this.deleteConfirmId = null;
  }

  /**
   * Start editing conversation title
   */
  startEditing(conversation: ConversationSummary, event: Event): void {
    event.stopPropagation();
    this.editingId = conversation.id;
    this.editTitle = conversation.title;
    this.menuOpenId = null;
  }

  /**
   * Save edited title
   */
  saveTitle(conversationId: string): void {
    if (!this.editTitle.trim()) {
      this.cancelEditing();
      return;
    }

    this.conversationListService.updateTitle(conversationId, this.editTitle.trim()).subscribe({
      next: () => {
        this.editingId = null;
        this.editTitle = '';
      },
      error: () => {
        // Error is handled by service, just reset UI
        this.cancelEditing();
      }
    });
  }

  /**
   * Cancel editing
   */
  cancelEditing(): void {
    this.editingId = null;
    this.editTitle = '';
  }

  /**
   * Handle enter key in edit input
   */
  onEditKeydown(event: KeyboardEvent, conversationId: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveTitle(conversationId);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditing();
    }
  }

  /**
   * Show delete confirmation
   */
  showDeleteConfirm(conversationId: string, event: Event): void {
    event.stopPropagation();
    this.deleteConfirmId = conversationId;
    this.menuOpenId = null;
  }

  /**
   * Confirm delete
   */
  confirmDelete(conversationId: string): void {
    // Prevent duplicate delete requests
    if (this.deletingId === conversationId) {
      return;
    }

    this.deletingId = conversationId;
    this.deleteError = null;

    this.conversationListService.deleteConversation(conversationId).subscribe({
      next: () => {
        this.deleteConfirmId = null;
        this.deletingId = null;

        // If we deleted the active conversation, emit event to create new one
        if (conversationId === this.activeConversationId) {
          this.newConversationRequested.emit();
        }
      },
      error: () => {
        this.deletingId = null;
        this.deleteError = 'Failed. Try again.';
        // Keep confirmation open so user can retry
      }
    });
  }

  /**
   * Cancel delete
   */
  cancelDelete(): void {
    // Don't allow cancel while delete is in progress
    if (this.deletingId) {
      return;
    }
    this.deleteConfirmId = null;
    this.deleteError = null;
  }

  /**
   * Close all menus
   */
  private closeAllMenus(): void {
    this.menuOpenId = null;
    this.editingId = null;
    // Only clear delete state if not actively deleting
    if (!this.deletingId) {
      this.deleteConfirmId = null;
    }
  }

  /**
   * Retry loading conversations
   */
  retry(): void {
    this.conversationListService.refresh();
  }

  /**
   * Format relative time for display
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /**
   * Check if conversation is active
   */
  isActive(conversationId: string): boolean {
    return conversationId === this.activeConversationId;
  }

  /**
   * Get the conversation with open menu (for the floating menu)
   */
  getMenuConversation(): ConversationSummary | undefined {
    if (!this.menuOpenId) return undefined;
    return this.conversations.find(c => c.id === this.menuOpenId);
  }
}
